//============================================================================
// Name        : api_logs.ts
// Purpose     : Implement handheld api logs
// Created     : 2020-01-31
// Author      : Senitron Team
// Copyright   : Copyright 2020 Senitron Corporation. All rights reserved.
//============================================================================

import {InitPage} from "../index";
import swal from "sweetalert";

declare var apiMonitorPath: string;

let currentSort = "status";

function formatTimeAgo(seconds: number): string {
    if (seconds < 60) {
        return "just now";
    }
    const minutes = Math.floor(seconds / 60);
    if (minutes < 120) {
        return `${minutes} min ago`;
    }
    const hours = Math.floor(seconds / 3600);
    if (hours < 48) {
        return `${hours} hours ago`;
    }
    const days = Math.floor(seconds / 86400);
    return `${days} days ago`;
}

function formatTimeLater(seconds: number): string {
    if (seconds <= 0) {
        return "soon";
    }
    const minutes = Math.ceil(seconds / 60);
    if (minutes < 120) {
        return `${minutes} min later`;
    }
    const hours = Math.ceil(seconds / 3600);
    if (hours < 48) {
        return `${hours} hours later`;
    }
    const days = Math.ceil(seconds / 86400);
    return `${days} days later`;
}

function refreshCheckLabels() {
    const now = Date.now();
    $(".am-next[data-next-check-at]").each((_, el) => {
        const iso = $(el).attr("data-next-check-at");
        if (!iso) {
            return;
        }
        const nextAt = Date.parse(iso);
        if (Number.isNaN(nextAt)) {
            return;
        }
        const seconds = Math.floor((nextAt - now) / 1000);
        $(el).find(".am-next-label").text(formatTimeLater(seconds));
    });
    $(".am-last-check[data-last-check-at]").each((_, el) => {
        const iso = $(el).attr("data-last-check-at");
        if (!iso) {
            return;
        }
        const lastAt = Date.parse(iso);
        if (Number.isNaN(lastAt)) {
            return;
        }
        const seconds = Math.floor((now - lastAt) / 1000);
        const baseLabel = formatTimeAgo(seconds);
        const suffix = $(el).find(".am-last-check-label").text().includes("outside schedule")
            ? " (outside schedule)"
            : "";
        $(el).find(".am-last-check-label").text(baseLabel + suffix);
    });
}

function lastCheckedSortValue($row: JQuery<HTMLElement>): number {
    const raw = String($row.attr("data-last-checked-at") || "").trim();
    if (!raw) {
        // Never run — treat as oldest so "oldest" puts these first and "newest" last.
        return 0;
    }
    const parsed = Date.parse(raw);
    return Number.isNaN(parsed) ? 0 : parsed;
}

function sortRows() {
    const tbody = $("#amTableBody");
    const rows = tbody.find(".am-row").get() as HTMLElement[];

    rows.sort((a, b) => {
        const $a = $(a);
        const $b = $(b);

        switch (currentSort) {
            case "name-asc":
                return String($a.data("name")).localeCompare(String($b.data("name")));
            case "name-desc":
                return String($b.data("name")).localeCompare(String($a.data("name")));
            case "last-check-desc":
                return lastCheckedSortValue($b) - lastCheckedSortValue($a);
            case "last-check-asc":
                return lastCheckedSortValue($a) - lastCheckedSortValue($b);
            case "status":
            default: {
                const aOrder = Number($a.data("statusOrder") ?? 99);
                const bOrder = Number($b.data("statusOrder") ?? 99);
                if (aOrder !== bOrder) {
                    return aOrder - bOrder;
                }
                return String($a.data("name")).localeCompare(String($b.data("name")));
            }
        }
    });

    tbody.append(rows);
}

function csvEscape(value: string): string {
    const text = String(value || "").replace(/"/g, '""');
    return `"${text}"`;
}

function exportVisibleRows() {
    const rows = $("#amTableBody .am-row:visible");
    if (!rows.length) {
        swal({ title: "Nothing to export", text: "No monitors match the current filters.", icon: "info" });
        return;
    }

    const headers = ["Status", "Name", "Method", "Endpoint", "Last Check", "Schedule", "Next Check", "Enabled"];
    const lines = [headers.map(csvEscape).join(",")];

    rows.each((_, row) => {
        const $row = $(row);
        const cells = $row.find("td");
        const status = $(cells[0]).find(".am-status").clone().children().remove().end().text().trim();
        const name = $(cells[1]).find(".am-endpoint-name").text().trim();
        const method = $(cells[1]).find(".am-method").text().trim();
        const endpoint = $(cells[1]).find(".am-endpoint-path").text().trim();
        const lastCheck = $(cells[2]).find(".am-last-check-label").text().trim();
        const scheduleDays = $(cells[3]).find(".am-day.active").map((__, el) => $(el).text().trim()).get().join("");
        const scheduleTime = $(cells[3]).find(".am-schedule-time").text().trim();
        const schedule = [scheduleDays, scheduleTime].filter(Boolean).join(" ");
        const nextCheck = $(cells[4]).find(".am-next-label").text().trim();
        const enabled = $(cells[5]).find(".am-toggle").hasClass("on") ? "Yes" : "No";

        lines.push(
            [status, name, method, endpoint, lastCheck, schedule, nextCheck, enabled]
                .map(csvEscape)
                .join(",")
        );
    });

    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.download = `api-monitors-${stamp}.csv`;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

function applyRowFilters() {
    const query = String($("#amSearch").val() || "").trim().toLowerCase();
    const statusFilter = String($(".am-filter.active").data("statusFilter") || "all");
    let visible = 0;

    $("#amTableBody .am-row").each((_, row) => {
        const $row = $(row);
        const haystack = String($row.data("search") || "");
        const status = String($row.data("status") || "");
        const matchesSearch = !query || haystack.indexOf(query) >= 0;
        const matchesStatus = statusFilter === "all" || status === statusFilter;
        const show = matchesSearch && matchesStatus;
        $row.toggle(show);
        if (show) {
            visible += 1;
        }
    });

    const total = $("#amTableBody .am-row").length;
    const countEl = $("#amCount");
    if (countEl.length) {
        if (visible === 0) {
            countEl.text("Showing 0 of " + total + " monitors");
        } else {
            countEl.text("Showing 1–" + visible + " of " + total + " monitors");
        }
    }
}

class ApiMonitor {
    constructor() {
        const syncEnabledUi = (enabled: boolean) => {
            const statusBtn = $("#amStatusToggle");
            statusBtn.attr("aria-checked", enabled ? "true" : "false");
            statusBtn.find(".status-label").text(enabled ? "Enabled" : "Disabled");
            $("#api_monitor_enabled").prop("checked", enabled);
        };

        const hydrateModalUi = () => {
            const w = window as unknown as { amHydrateApiMonitorModal?: () => void };
            if (typeof w.amHydrateApiMonitorModal === "function") {
                w.amHydrateApiMonitorModal();
            }
        };

        // Auto populate hours from previous checked
        $("input[type=checkbox]").on("change", (e) => {
            const attrName = $(e.target).attr("name");
            const name = attrName.split("[")[1].split("]")[0];

            const lastValues = this.getLastValues();

            $(`input[name='api_monitor[${name}_start]']`).val(lastValues.startHour);
            $(`input[name='api_monitor[${name}_end]']`).val(lastValues.endHour);
        });

        const openModal = (id: string) => {
            document.getElementById(id)?.classList.add("open");
        };

        const closeModal = (el: Element | null) => {
            el?.closest(".am-modal-backdrop")?.classList.remove("open");
        };

        $(document).on("click", "[data-open-modal]", (e) => {
            e.preventDefault();
            const modalId = $(e.currentTarget).data("openModal");
            if (modalId) {
                openModal(String(modalId));
            }
        });

        $(document).on("click", ".am-run-check", (e) => {
            e.preventDefault();
            const btn = $(e.currentTarget);
            const url = btn.data("checkUrl");
            const apiName = btn.data("apiName") || "";
            if (!url) {
                return;
            }
            $("#runCheckApiName").text(String(apiName));
            $("#runCheckConfirm").attr("href", String(url));
            openModal("runCheckModal");
        });

        $(document).on("click", ".am-modal-backdrop", (e) => {
            if (e.target === e.currentTarget) {
                closeModal(e.currentTarget as Element);
            }
        });

        $(document).on("click", ".am-modal-backdrop [data-close]", (e) => {
            closeModal(e.currentTarget as Element);
        });

        $(document).on("keydown", (e) => {
            if (e.key === "Escape") {
                document.querySelectorAll(".am-modal-backdrop.open").forEach((m) => m.classList.remove("open"));
            }
        });

        $(".show-add-apimonitor").on("click", () => {
            const editDialog = $("#edit-apimonitor");
            const form       = editDialog.find("form");
            const apiSelect  = $("#api");

            editDialog.modal("show");
            $("#api_monitor_id").val("");
            $("#amModalTitle").text(String($("#amModalTitle").data("addTitle") || "Add API Monitor"));
            if (apiSelect.hasClass("select2-hidden-accessible")) {
                apiSelect.select2("destroy");
            }
            apiSelect.select2({dropdownParent: editDialog, width: "100%"});

            // Set form path
            form.prop("action", apiMonitorPath);
            form.find("input[name='_method']").val("post");

            // Reset dialog fields
            apiSelect.val("").trigger("change");

            $("#am_monday").prop("checked", false);
            $("#am_monday_start").val("");
            $("#am_monday_end").val("");

            $("#am_tuesday").prop("checked", false);
            $("#am_tuesday_start").val("");
            $("#am_tuesday_end").val("");

            $("#am_wednesday").prop("checked", false);
            $("#am_wednesday_start").val("");
            $("#am_wednesday_end").val("");

            $("#am_thursday").prop("checked", false);
            $("#am_thursday_start").val("");
            $("#am_thursday_end").val("");

            $("#am_friday").prop("checked", false);
            $("#am_friday_start").val("");
            $("#am_friday_end").val("");

            $("#am_saturday").prop("checked", false);
            $("#am_saturday_start").val("");
            $("#am_saturday_end").val("");

            $("#am_sunday").prop("checked", false);
            $("#am_sunday_start").val("");
            $("#am_sunday_end").val("");

            $("#am_notify_emails").val("");

            $("#am_check_hour_interval").val(3);

            syncEnabledUi(true);
            hydrateModalUi();
        });

        // Row action menu (three dots)
        $(document).on("click", ".am-menu-trigger", (e) => {
            e.stopPropagation();
            const wrap = $(e.currentTarget).closest(".am-menu-wrap");
            const menu = wrap.find(".am-menu");
            const isOpen = menu.hasClass("open");
            $(".am-menu").removeClass("open am-menu-up");
            if (!isOpen) {
                menu.addClass("open");
                const menuEl = menu[0];
                const triggerRect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                const menuRect = menuEl.getBoundingClientRect();
                const tableWrap = wrap.closest(".am-table-wrap")[0];
                const containerBottom = tableWrap
                    ? tableWrap.getBoundingClientRect().bottom
                    : window.innerHeight;
                const limit = Math.min(containerBottom, window.innerHeight);
                if (menuRect.bottom > limit - 8 && triggerRect.top > menuRect.height + 16) {
                    menu.addClass("am-menu-up");
                }
            }
        });

        $(document).on("click", () => {
            $(".am-menu").removeClass("open am-menu-up");
        });

        $(document).on("click", ".am-menu", (e) => {
            e.stopPropagation();
        });

        $(document).on("click", ".am-delete-monitor", (e) => {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            $(".am-menu").removeClass("open am-menu-up");

            const anchor = e.currentTarget as HTMLAnchorElement;
            const url = anchor.getAttribute("href");
            if (!url) {
                return;
            }

            const message =
                $(anchor).data("confirmMessage") ||
                "Are you sure you want to delete this monitor?";

            swal({
                title: "Are you sure?",
                text: String(message),
                icon: "warning",
                closeOnClickOutside: false,
                buttons: {
                    cancel: {
                        text: "Cancel",
                        value: null,
                        visible: true,
                        className: "sn-btn sn-btn-sm sn-btn-silver",
                        closeModal: true,
                    },
                    confirm: {
                        text: "Delete",
                        value: true,
                        visible: true,
                        className: "sn-btn sn-btn-sm sn-btn-red",
                        closeModal: true,
                    },
                },
                dangerMode: true,
            }).then((willDelete) => {
                if (!willDelete) {
                    return;
                }

                const token =
                    $('meta[name="csrf-token"]').attr("content") ||
                    ($('[name=authenticity_token]').val() as string);

                const form = $("<form>", { method: "POST", action: url, css: { display: "none" } });
                form.append($("<input>", { type: "hidden", name: "_method", value: "delete" }));
                form.append($("<input>", { type: "hidden", name: "authenticity_token", value: token }));
                $("body").append(form);
                form.trigger("submit");
            });
        });

        // Client-side search and status filter
        $("#amSearch").on("input", () => {
            applyRowFilters();
        });

        $(".am-filter").on("click", (e) => {
            $(".am-filter").removeClass("active");
            $(e.currentTarget).addClass("active");
            applyRowFilters();
        });

        $("#amRefresh").on("click", (e) => {
            const btn = $(e.currentTarget);
            btn.addClass("spinning").prop("disabled", true);
            window.location.reload();
        });

        $(document).on("click", ".am-sort-trigger", (e) => {
            e.stopPropagation();
            const wrap = $(e.currentTarget).closest(".am-menu-wrap");
            const menu = wrap.find(".am-sort-menu");
            const isOpen = menu.hasClass("open");
            $(".am-menu").removeClass("open am-menu-up");
            if (!isOpen) {
                menu.addClass("open");
            }
        });

        $(document).on("click", ".am-sort-option", (e) => {
            e.stopPropagation();
            $(".am-sort-option").removeClass("active");
            const option = $(e.currentTarget);
            option.addClass("active");
            currentSort = String(option.data("sort") || "status");
            $(".am-sort-menu").removeClass("open am-menu-up");
            sortRows();
            applyRowFilters();
        });

        $("#amExport").on("click", () => {
            if (!$("#amTableBody .am-row:visible").length) {
                swal({
                    title: "Nothing to export",
                    text: "No monitors match the current filters.",
                    icon: "info",
                });
                return;
            }

            const stamp = new Date().toISOString().slice(0, 10);
            $("#amExportFilename").text(`api-monitors-${stamp}.csv`);
            openModal("amExportModal");
        });

        $("#amExportConfirm").on("click", () => {
            document.getElementById("amExportModal")?.classList.remove("open");
            exportVisibleRows();
        });

        $(document).on("click", ".show-edit-apimonitor", (e) => {
            e.preventDefault();
            const currentTarget = e.currentTarget as HTMLElement;
            $(".am-menu").removeClass("open am-menu-up");
            const recordAttr    = $(currentTarget).attr("data-record");
            const apiMonitor    = typeof recordAttr === "string" ? JSON.parse(recordAttr) : $(currentTarget).data("record");

            const editDialog = $("#edit-apimonitor");
            const form       = editDialog.find("form");
            const target     = $(currentTarget);
            const apiSelect  = $("#api");

            // Set form path
            form.prop("action", apiMonitorPath + "/" + apiMonitor.id);
            form.find("input[name='_method']").val("patch");

            editDialog.modal("show");
            $("#api_monitor_id").val(target.data("id"));
            $("#amModalTitle").text(String($("#amModalTitle").data("editTitle") || "Edit API Monitor"));
            if (apiSelect.hasClass("select2-hidden-accessible")) {
                apiSelect.select2("destroy");
            }
            apiSelect.select2({dropdownParent: editDialog, width: "100%"});

            // Populate API monitor
            apiSelect.val(apiMonitor.api).trigger("change");

            $("#am_monday").prop("checked", !!apiMonitor.monday);
            $("#am_monday_start").val(apiMonitor.monday_start);
            $("#am_monday_end").val(apiMonitor.monday_end);

            $("#am_tuesday").prop("checked", !!apiMonitor.tuesday);
            $("#am_tuesday_start").val(apiMonitor.tuesday_start);
            $("#am_tuesday_end").val(apiMonitor.tuesday_end);

            $("#am_wednesday").prop("checked", !!apiMonitor.wednesday);
            $("#am_wednesday_start").val(apiMonitor.wednesday_start);
            $("#am_wednesday_end").val(apiMonitor.wednesday_end);

            $("#am_thursday").prop("checked", !!apiMonitor.thursday);
            $("#am_thursday_start").val(apiMonitor.thursday_start);
            $("#am_thursday_end").val(apiMonitor.thursday_end);

            $("#am_friday").prop("checked", !!apiMonitor.friday);
            $("#am_friday_start").val(apiMonitor.friday_start);
            $("#am_friday_end").val(apiMonitor.friday_end);

            $("#am_saturday").prop("checked", !!apiMonitor.saturday);
            $("#am_saturday_start").val(apiMonitor.saturday_start);
            $("#am_saturday_end").val(apiMonitor.saturday_end);

            $("#am_sunday").prop("checked", !!apiMonitor.sunday);
            $("#am_sunday_start").val(apiMonitor.sunday_start);
            $("#am_sunday_end").val(apiMonitor.sunday_end);

            $("#am_check_hour_interval").val(apiMonitor.check_hour_interval);
            $("#am_notify_emails").val(apiMonitor.notify_emails);

            const enabled =
                apiMonitor.enabled === true ||
                apiMonitor.enabled === 1 ||
                apiMonitor.enabled === "1" ||
                apiMonitor.enabled === "true";
            syncEnabledUi(enabled);
            hydrateModalUi();
        });
    }

    hourValid(hour) {
        return hour != "" && hour != "00:00:00";
    }

    getLastCheckedDay() {
        let day = "";
        if ($("input[name='api_monitor[monday]']").is(":checked") === true) {
            const startHour = $("input[name='api_monitor[monday_start]']").val();
            const endHour   = $("input[name='api_monitor[monday_end]']").val();

            if (this.hourValid(startHour) || this.hourValid(endHour)) {
                day = "monday";
            }
        }
        if ($("input[name='api_monitor[tuesday]']").is(":checked") === true) {
            const startHour = $("input[name='api_monitor[tuesday_start]']").val();
            const endHour   = $("input[name='api_monitor[tuesday_end]']").val();

            if (this.hourValid(startHour) || this.hourValid(endHour)) {
                day = "tuesday";
            }
        }
        if ($("input[name='api_monitor[wednesday]']").is(":checked") === true) {
            const startHour = $("input[name='api_monitor[wednesday_start]']").val();
            const endHour   = $("input[name='api_monitor[wednesday_end]']").val();

            if (this.hourValid(startHour) || this.hourValid(endHour)) {
                day = "wednesday";
            }
        }
        if ($("input[name='api_monitor[thursday]']").is(":checked") === true) {
            const startHour = $("input[name='api_monitor[thursday_start]']").val();
            const endHour   = $("input[name='api_monitor[thursday_end]']").val();

            if (this.hourValid(startHour) || this.hourValid(endHour)) {
                day = "thursday";
            }
        }
        if ($("input[name='api_monitor[friday]']").is(":checked") === true) {
            const startHour = $("input[name='api_monitor[friday_start]']").val();
            const endHour   = $("input[name='api_monitor[friday_end]']").val();

            if (this.hourValid(startHour) || this.hourValid(endHour)) {
                day = "friday";
            }
        }
        if ($("input[name='api_monitor[saturday]']").is(":checked") === true) {
            const startHour = $("input[name='api_monitor[saturday_start]']").val();
            const endHour   = $("input[name='api_monitor[saturday_end]']").val();

            if (this.hourValid(startHour) || this.hourValid(endHour)) {
                day = "saturday";
            }
        }
        if ($("input[name='api_monitor[sunday]']").is(":checked") === true) {
            const startHour = $("input[name='api_monitor[sunday_start]']").val();
            const endHour   = $("input[name='api_monitor[sunday_end]']").val();

            if (this.hourValid(startHour) || this.hourValid(endHour)) {
                day = "sunday";
            }
        }
        return day;
    }

    getLastValues() {
        const lastDay = this.getLastCheckedDay();

        let startHour = 0;
        let endHour = 0;

        console.log(lastDay);
        switch (lastDay) {
            case "monday":
                startHour = $("input[name='api_monitor[monday_start]']").val() as number;
                endHour   = $("input[name='api_monitor[monday_end]']").val() as number;
                break;
            case "tuesday":
                startHour = $("input[name='api_monitor[tuesday_start]']").val() as number;
                endHour   = $("input[name='api_monitor[tuesday_end]']").val() as number;
                break;
            case "wednesday":
                startHour = $("input[name='api_monitor[wednesday_start]']").val() as number;
                endHour   = $("input[name='api_monitor[wednesday_end]']").val() as number;
                break;
            case "thursday":
                startHour = $("input[name='api_monitor[thursday_start]']").val() as number;
                endHour   = $("input[name='api_monitor[thursday_end]']").val() as number;
                break;
            case "friday":
                startHour = $("input[name='api_monitor[friday_start]']").val() as number;
                endHour   = $("input[name='api_monitor[friday_end]']").val() as number;
                break;
            case "saturday":
                startHour = $("input[name='api_monitor[saturday_start]']").val() as number;
                endHour   = $("input[name='api_monitor[saturday_end]']").val() as number;
                break;
            case "sunday":
                startHour = $("input[name='api_monitor[sunday_start]']").val() as number;
                endHour   = $("input[name='api_monitor[sunday_end]']").val() as number;
                break;
        }
        return { startHour, endHour };
    }
}

export function LoadPage() {
    InitPage("api_monitor index", () => {
        const c = new ApiMonitor();
        sortRows();
        applyRowFilters();
        refreshCheckLabels();
        window.setInterval(refreshCheckLabels, 60000);
    });
}
